import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, API } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ArrowLeft, Download, Calendar, DollarSign, Package, Plus, Trash2, Pencil } from 'lucide-react';
import { DatePickerField } from '../components/DatePickerField';
import { format, subMonths } from 'date-fns';

export default function SupplierStatement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [statement, setStatement] = useState(null);
  
  // Filter state
  const [startDate, setStartDate] = useState(subMonths(new Date(), 3));
  const [endDate, setEndDate] = useState(new Date());
  const [isLifetime, setIsLifetime] = useState(false);

  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentData, setPaymentData] = useState({
    amount: '',
    method: '',
    transaction_code: '',
    remarks: ''
  });

  // Edit payment state
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentDate, setEditPaymentDate] = useState(new Date());
  const [editPaymentData, setEditPaymentData] = useState({
    supplier_id: '',
    amount: '',
    method: '',
    transaction_code: '',
    remarks: ''
  });
  const [allSuppliers, setAllSuppliers] = useState([]);

  useEffect(() => {
    fetchStatement();
  }, [id, startDate, endDate, isLifetime]);

  useEffect(() => {
    api.get('/suppliers').then(res => setAllSuppliers(res.data)).catch(() => {});
  }, []);

  const fetchStatement = async () => {
    try {
      let url = `/suppliers/${id}/statement`;
      if (!isLifetime) {
        const params = new URLSearchParams({
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd')
        });
        url += `?${params.toString()}`;
      }
      
      const res = await api.get(url);
      setStatement(res.data);
    } catch (error) {
      toast.error('Failed to load supplier statement');
      navigate('/suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      let pdfUrl = `${API}/suppliers/${id}/statement/pdf`;
      if (!isLifetime) {
        const params = new URLSearchParams({
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd')
        });
        pdfUrl += `?${params.toString()}`;
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(pdfUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to download PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SupplierStatement_${statement?.supplier?.name?.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Statement downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentData.amount || !paymentData.method) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      await api.post(`/suppliers/${id}/payments`, {
        date: format(paymentDate, 'yyyy-MM-dd'),
        amount: parseFloat(paymentData.amount),
        method: paymentData.method,
        transaction_code: paymentData.transaction_code || null,
        remarks: paymentData.remarks || null
      });
      toast.success('Payment recorded');
      setPaymentDialogOpen(false);
      setPaymentData({ amount: '', method: '', transaction_code: '', remarks: '' });
      setPaymentDate(new Date());
      fetchStatement();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await api.delete(`/suppliers/${id}/payments/${paymentId}`);
      toast.success('Payment deleted');
      fetchStatement();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const openEditPayment = (payment) => {
    setEditingPayment(payment);
    setEditPaymentDate(new Date(payment.date + 'T00:00:00'));
    setEditPaymentData({
      supplier_id: payment.supplier_id || id,
      amount: String(payment.amount),
      method: payment.method,
      transaction_code: payment.transaction_code || '',
      remarks: payment.remarks || ''
    });
    setEditPaymentDialogOpen(true);
  };

  const handleEditPayment = async (e) => {
    e.preventDefault();
    if (!editPaymentData.amount || !editPaymentData.method) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      const newSupplierId = editPaymentData.supplier_id !== id ? editPaymentData.supplier_id : undefined;
      let url = `/suppliers/${id}/payments/${editingPayment.id}`;
      if (newSupplierId) url += `?new_supplier_id=${newSupplierId}`;
      await api.put(url, {
        date: format(editPaymentDate, 'yyyy-MM-dd'),
        amount: parseFloat(editPaymentData.amount),
        method: editPaymentData.method,
        transaction_code: editPaymentData.transaction_code || null,
        remarks: editPaymentData.remarks || null
      });
      toast.success('Payment updated');
      setEditPaymentDialogOpen(false);
      setEditingPayment(null);
      fetchStatement();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-KE').format(num || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  if (!statement) return null;

  const { supplier, summary, purchases, payments } = statement;

  return (
    <div className="space-y-6" data-testid="supplier-statement-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/suppliers')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Supplier Statement</h1>
            <p className="text-slate-500 mt-1">{supplier.name} - {supplier.location || 'No location'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setPaymentDialogOpen(true)}
            className="gap-2"
            data-testid="record-payment-btn"
          >
            <Plus className="h-4 w-4" />
            Record Payment
          </Button>
          <Button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="btn-primary gap-2"
            data-testid="download-pdf-btn"
          >
            <Download className="h-4 w-4" />
            {downloading ? 'Downloading...' : 'PDF'}
          </Button>
        </div>
      </div>

      {/* Filter Indicator */}
      {summary.is_filtered ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2" data-testid="filter-indicator">
          <p className="text-sm text-amber-800 font-medium">
            Showing filtered results: {format(startDate, 'dd MMM yyyy')} → {format(endDate, 'dd MMM yyyy')}
          </p>
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2" data-testid="lifetime-indicator">
          <p className="text-sm text-slate-600 font-medium">Showing lifetime results</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600">Total Purchases</p>
            <p className="text-xl sm:text-2xl font-bold text-blue-700" data-testid="summary-total-purchases">{formatCurrency(summary.total_purchases)}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-4">
            <p className="text-sm text-green-600">Total Paid</p>
            <p className="text-xl sm:text-2xl font-bold text-green-700">{formatCurrency(summary.total_paid)}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="p-4">
            <p className="text-sm text-red-600">Balance</p>
            <p className="text-xl sm:text-2xl font-bold text-red-700">{formatCurrency(summary.total_balance)}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50">
          <CardContent className="p-4">
            <p className="text-sm text-slate-600">Purchases Count</p>
            <p className="text-xl sm:text-2xl font-bold text-slate-700">{formatNumber(summary.number_of_purchases)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>From:</Label>
              <DatePickerField
                date={startDate}
                onDateChange={(d) => { setStartDate(d); setIsLifetime(false); }}
                buttonClassName="w-32 sm:w-40 justify-start text-left font-normal"
                displayFormat="MMM d, yyyy"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label>To:</Label>
              <DatePickerField
                date={endDate}
                onDateChange={(d) => { setEndDate(d); setIsLifetime(false); }}
                buttonClassName="w-32 sm:w-40 justify-start text-left font-normal"
                displayFormat="MMM d, yyyy"
              />
            </div>
            <Button
              variant={isLifetime ? "default" : "outline"}
              size="sm"
              onClick={() => setIsLifetime(true)}
              data-testid="lifetime-btn"
              className={isLifetime ? "bg-[#1B4D3E] hover:bg-[#163d32]" : ""}
            >
              Lifetime
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Purchases Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Purchases (Record Only)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Factory</TableHead>
                <TableHead className="text-right">Kg Received</TableHead>
                <TableHead className="text-right">Total Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purchases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No purchases found for this period
                  </TableCell>
                </TableRow>
              ) : (
                purchases.map((purchase) => (
                  <TableRow key={purchase.id}>
                    <TableCell>{purchase.date}</TableCell>
                    <TableCell className="font-medium">{purchase.batch_no}</TableCell>
                    <TableCell>
                      {purchase.factory ? <Badge variant="outline">{purchase.factory}</Badge> : '-'}
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(purchase.kg_received)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(purchase.total_cost)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payments Made Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Payments Made
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[90px]">Date</TableHead>
                <TableHead className="text-right min-w-[100px]">Amount</TableHead>
                <TableHead className="min-w-[80px]">Method</TableHead>
                <TableHead className="min-w-[120px]">T. Code</TableHead>
                <TableHead className="min-w-[150px]">Remarks</TableHead>
                <TableHead className="text-center min-w-[60px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(!payments || payments.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No payments recorded
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => (
                  <TableRow key={payment.id} className="bg-green-50/50">
                    <TableCell className="whitespace-nowrap">{payment.date}</TableCell>
                    <TableCell className="text-right font-medium text-green-600 whitespace-nowrap">
                      {formatCurrency(payment.amount)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{payment.method}</TableCell>
                    <TableCell className="font-mono text-sm break-all max-w-[150px]">{payment.transaction_code || '-'}</TableCell>
                    <TableCell className="text-slate-500 break-words max-w-[200px] whitespace-normal">{payment.remarks || '-'}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditPayment(payment)} className="text-blue-500 hover:text-blue-600" data-testid={`edit-payment-${payment.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeletePayment(payment.id)} className="text-red-500 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Contact Info */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Phone</p>
              <p className="font-medium">{supplier.phone || '-'}</p>
            </div>
            <div>
              <p className="text-slate-500">Location</p>
              <p className="font-medium">{supplier.location || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment to {supplier.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <DatePickerField date={paymentDate} onDateChange={setPaymentDate} />
            </div>
            <div className="space-y-2">
              <Label>Amount (KES) *</Label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label>Method *</Label>
              <Select value={paymentData.method} onValueChange={(v) => setPaymentData({ ...paymentData, method: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="M-Pesa">M-Pesa</SelectItem>
                  <SelectItem value="Bank">Bank Transfer</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Transaction Code</Label>
              <Input
                value={paymentData.transaction_code}
                onChange={(e) => setPaymentData({ ...paymentData, transaction_code: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input
                value={paymentData.remarks}
                onChange={(e) => setPaymentData({ ...paymentData, remarks: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <Button type="submit" className="w-full btn-primary">
              Record Payment
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={editPaymentDialogOpen} onOpenChange={setEditPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditPayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Supplier</Label>
              <Select value={editPaymentData.supplier_id} onValueChange={(v) => setEditPaymentData({ ...editPaymentData, supplier_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  {allSuppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <DatePickerField date={editPaymentDate} onDateChange={setEditPaymentDate} />
            </div>
            <div className="space-y-2">
              <Label>Amount (KES) *</Label>
              <Input type="number" value={editPaymentData.amount} onChange={(e) => setEditPaymentData({ ...editPaymentData, amount: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Method *</Label>
              <Select value={editPaymentData.method} onValueChange={(v) => setEditPaymentData({ ...editPaymentData, method: v })}>
                <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="M-Pesa">M-Pesa</SelectItem>
                  <SelectItem value="Bank">Bank Transfer</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Transaction Code</Label>
              <Input value={editPaymentData.transaction_code} onChange={(e) => setEditPaymentData({ ...editPaymentData, transaction_code: e.target.value })} placeholder="Optional" />
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input value={editPaymentData.remarks} onChange={(e) => setEditPaymentData({ ...editPaymentData, remarks: e.target.value })} placeholder="Optional" />
            </div>
            <Button type="submit" className="w-full btn-primary">Update Payment</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
